import './formEntryNew.less';

import React, { CSSProperties, ReactNode } from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';

import { ModelValidator, ValidatableModel, ValidationState } from 'framework/mobx-integration';
import { computed, makeAutoObservable, observable, runInAction } from 'mobx';
import { useStore } from 'core/react/useStore';
import b_ from 'b_';
import { translator } from 'core/localization';
import { AntTooltip } from '../ant/antTooltip';

const b = b_.with('form-entry-new');

const i = translator();

export class FormEntryProps<TModel extends object, TModelField extends keyof TModel> {
	label?: React.ReactNode;
	placeholder?: React.ReactNode;
	title?: 'string';
	children: React.ReactNode;
	width?: number | string;
	minWidth?: number | string;

	highlight?: boolean;

	className?: string;

	id?: string;
	style?: CSSProperties;

	model?: TModel;
	modelField?: TModelField;
	onChange?: (value: TModel[TModelField]) => void;
	value?: TModel[TModelField];

	headerAdditionalContent?: React.ReactNode;

	disabled?: boolean;

	validator?: ModelValidator<TModel>;

	changeOnBlur?: boolean;
	alwaysWithValue?: boolean;

	noUi?: boolean;

	hasPlaceholder?: boolean;

	oldStyle?: boolean;

	//if you do not want Form entry to bind the first children to a provided model but only want to show validation result.
	// This might be useful when there are several inputs inside form entry
	validationOnly?: boolean;

	constructor() {
		makeAutoObservable(this, {
			children: observable.ref,
			headerAdditionalContent: observable.ref,
			label: observable.ref,
		});
	}
}

class FormEntryNewStore<TModel extends ValidatableModel<TModel> | object, TModelField extends keyof TModel> {
	focused: boolean;
	props: FormEntryProps<TModel, TModelField> = new FormEntryProps<TModel, TModelField>();
	tempValue: TModel[TModelField] = null;
	initialValueChanged: boolean = false;

	constructor() {
		makeAutoObservable(this, {
			validationState: computed({ keepAlive: true }),
		});
	}

	get validator() {
		if (this.props.validator) return this.props.validator;

		if (this.props.model == null) return null;

		return 'validator' in this.props.model ? this.props.model.validator : null;
	}

	get fieldValidator() {
		return this.validator?.getFieldValidator(this.props.modelField);
	}

	get hasValidation() {
		return this.fieldValidator != null;
	}

	get required() {
		if (this.fieldValidator == null) return false;

		return this.fieldValidator.requiredValidationState != ValidationState.Valid;
	}

	get validationState() {
		if (this.fieldValidator == null) return ValidationState.Valid;

		return this.fieldValidator.revalidated && (this.initialValueChanged || this.validator.submitted)
			? this.fieldValidator.validationState
			: ValidationState.Valid;
	}

	get validationMessage() {
		return this.validationState != ValidationState.Valid
			? (this.validator?.getErrors(this.props.modelField).join() ?? '')
			: '';
	}

	get value() {
		if (this.props.value != null) return this.props.value;

		if (this.props.model == null) return undefined;

		if (this.focused && this.props.changeOnBlur) {
			return this.tempValue;
		}

		return this.props.model[this.props.modelField];
	}

	get hasValue() {
		return (
			(Array.isArray(this.value)
				? this.value.length > 0
				: (this.value as never as number) === 0 || (this.value as never as boolean) === false
					? true
					: !!this.value) || this.props.alwaysWithValue
		);
	}

	get onChange() {
		if (this.props.onChange) return this.props.onChange;

		if (this.props.validationOnly) return undefined;

		if (this.props.changeOnBlur) {
			return (value: TModel[TModelField]) => (this.tempValue = value);
		}

		if (!this.props.model) return undefined;

		return (value: TModel[TModelField]) => runInAction(() => (this.props.model[this.props.modelField] = value));
	}

	get style() {
		const fields = ['width', 'minWidth'] as const;
		if (fields.every((f) => this.props[f] == null)) return this.props.style;

		const result: CSSProperties = {
			...(this.props.style ?? {}),
		};

		fields.forEach((f) => {
			if (Number.isInteger(this.props[f])) {
				result[f] = this.props[f] + 'px';
			} else {
				result[f] = this.props[f];
			}
		});

		return result;
	}

	get displayPlaceholder() {
		return this.props.hasPlaceholder && this.props.placeholder;
	}

	getProcessedChildren() {
		const children = React.Children.toArray(this.props.children);
		if (!React.isValidElement(children[0])) return children;

		const { onFocus, onBlur, readOnly } = children[0].props ?? {};
		const propsOverrides: {
			[index: string]: object | TModel[TModelField] | boolean | string | React.ReactNode;
		} = {
			onFocus: (ev: React.FocusEvent) => {
				if (readOnly) return;

				this.tempValue = this.value;

				this.focused = true;
				onFocus?.(ev);
			},
			onBlur: (ev: React.FocusEvent) => {
				this.focused = false;

				if (!!this.props.model && this.props.model[this.props.modelField] != this.tempValue) {
					this.initialValueChanged = true;
				}
				if (!!this.props.model && this.props.changeOnBlur) {
					this.props.model[this.props.modelField] = this.tempValue;
					this.tempValue = null;
				}
				onBlur?.(ev);
			},
			placeholder: undefined,
		};

		if (this.props.validationOnly !== true) {
			if (this.value !== undefined) {
				propsOverrides.value = this.value;
			}

			if (this.onChange !== undefined) {
				propsOverrides.onChange = this.onChange;
			}

			if (this.props.disabled !== undefined) {
				propsOverrides.disabled = this.props.disabled;
			}
		}

		if (this.props.placeholder) {
			propsOverrides.placeholder = this.props.placeholder;
		}

		if ((this.props.label && !this.focused && !this.props.hasPlaceholder) || this.hasValue) {
			propsOverrides.placeholder = '';
		}

		//We are assuming there that the first child element is an input so we can pass value and onChange
		children[0] = React.cloneElement(children[0], propsOverrides);

		return children;
	}
}

export const FormEntryNew = observer(
	<TModel extends ValidatableModel<TModel> | object, TModelField extends keyof TModel>(
		props: FormEntryProps<TModel, TModelField>,
	) => {
		const store = useStore(() => new FormEntryNewStore<TModel, TModelField>(), {
			props: props,
		});

		const blockClasses: { [index: string]: boolean | string } = {
			'focused': store.focused,
			'disabled': props.disabled,
			'has-validation': store.hasValidation,
			'required': store.required,
			'validation-state': store.validationState,
			'has-value': store.hasValue || store.displayPlaceholder,
			'old-style': props.oldStyle,
			'highlight': props.highlight,
		};

		const rootClasses = classnames(b(blockClasses), props.className);

		if (props.noUi) {
			return store.getProcessedChildren();
		}

		return (
			<div id={props.id} className={rootClasses} style={{ ...store.style }} title={props.title}>
				{props.label && (
					<div className={b('label')}>
						{store.hasValue || store.displayPlaceholder
							? props.label
							: (props.placeholder ?? (props.hasPlaceholder ? '' : props.label))}
						{store.required && ' *'}
					</div>
				)}
				{props.headerAdditionalContent && (
					<div className={b('additional-content')}>{props.headerAdditionalContent}</div>
				)}

				<div className={b('field')}>{store.getProcessedChildren()}</div>

				{store.validationMessage && <p className={b('validation-message')}>{store.validationMessage}</p>}
			</div>
		);
	},
);

export type HelpIconProps = {
	tooltip?: string | ReactNode;
	className?: string;
	style?: React.CSSProperties;
};

export const HelpIcon = observer((props: HelpIconProps) => {
	const cn = classnames(props.className, b('help-icon'), 'glyphicons', 'question-sign');
	const icon = <i className={cn} style={props.style} />;
	if (props.tooltip) {
		return <AntTooltip title={props.tooltip}>{icon}</AntTooltip>;
	}
	return icon;
});

export function useGenericModel<T>(getCallback: () => T, setCallback: (value: T) => void, deps: object[]) {
	return React.useMemo(() => {
		return new GenericModel(getCallback, setCallback);
	}, deps);
}

class GenericModel<T> {
	constructor(
		private getCallback: () => T,
		private setCallback: (value: T) => void,
	) {
		makeAutoObservable(this);
	}

	get value() {
		return this.getCallback();
	}

	set value(value: T) {
		this.setCallback(value);
	}
}
